import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { API } from 'network/api';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectToken } from 'store/auth/selectors';
import { getRoleFromToken } from 'utils/helpers';

interface Agronomist {
  id: string;
  email: string;
  name: string;
  role: string;
  country: string;
}

const agRole = {
  agSales: 'Agronomist - Sales,Jr Agronomist',
  agAwd: 'Agronomist - AWD',
  awdWorker: 'AWD Worker',
  sr: 'TM,AWD Manager,Sr Agronomist',
  jr: 'Agronomist - Sales,Jr Agronomist,Agronomist - AWD',
  srPm: 'TM,PM',
  all: 'TM,AWD Worker,Agronomist - Sales,Agronomist - AWD,PM,Sr Agronomist,Jr Agronomist',
};

export type Config = {
  role?: keyof typeof agRole;
  labelKey?: 'email' | 'id' | 'name';
  valueKey?: 'email' | 'id' | 'name';
};

// User with the role in key can see agronomist with the roles in value
const userRoleToAgRoleMappings = {
  TM: agRole.agSales,
  'Sr Agronomist': agRole.jr,
  'AWD Manager': 'Agronomist - AWD,JR Agronomist,AWD Worker',
  'Agronomist - Sales': agRole.agSales,
  'Agronomist - AWD': agRole.agAwd,
};

const useAgronomistList = (country?: string, config?: Config) => {
  const userRole = getRoleFromToken(useSelector(selectToken));

  const role =
    agRole?.[config?.role as keyof typeof agRole] ||
    userRoleToAgRoleMappings?.[
      userRole as keyof typeof userRoleToAgRoleMappings
    ] ||
    agRole.jr;

  const queryParams = useMemo(
    () => ({
      'role.in': role,
      sort: 'name,asc',
      page: 0,
      size: 9999,
      ...(country && { 'country.equals': country }),
    }),
    [country, config?.role],
  );
  const { data, isLoading, refetch, error } = useQuery<
    AxiosResponse<Agronomist[]>
  >({
    queryKey: ['agronomist', queryParams],
    placeholderData: keepPreviousData,
    queryFn: () => API.get('/user/search', { params: queryParams }),
    enabled: !!country,
    staleTime: 1000 * 30,
  });

  const agronomistFilterOptions = useMemo(
    () =>
      data?.data?.map((agronomist) => ({
        label: config?.labelKey
          ? agronomist?.[config.labelKey]
          : agronomist?.name,
        value: config?.valueKey
          ? agronomist?.[config.valueKey]
          : agronomist?.id,
      })),
    [data, config?.labelKey, config?.valueKey],
  );

  return { agronomistFilterOptions, data, refetch, isLoading, error };
};

export default useAgronomistList;
